import { account } from './lib/api';
import { currentUser, initials, saveToken } from './lib/session';

/** Where to go after signing in, when nothing asked to return to a page. */
const AFTER_SIGN_IN = '/app/';

let returnTo = null;

function setupHeader(user) {
  const header = document.getElementById('site-header');
  const menu = document.getElementById('site-menu');
  const toggle = document.getElementById('menu-toggle');
  if (!header) return;

  document.querySelectorAll('[data-signed-in]').forEach((el) => { el.hidden = !user; });
  document.querySelectorAll('[data-signed-out]').forEach((el) => { el.hidden = Boolean(user); });
  if (user) {
    document.querySelectorAll('[data-user-name]').forEach((el) => { el.textContent = user.name; });
    document.querySelectorAll('[data-user-initials]').forEach((el) => { el.textContent = initials(user.name); });
  }

  const page = document.body.dataset.page;
  document.querySelectorAll(`[data-nav="${page}"]`).forEach((el) => el.setAttribute('aria-current', 'page'));

  toggle.addEventListener('click', () => {
    const open = menu.classList.toggle('hidden') === false;
    menu.classList.toggle('flex', open);
    toggle.setAttribute('aria-expanded', String(open));
  });
  menu.addEventListener('click', (event) => {
    if (event.target.closest('a') && window.innerWidth < 1024) {
      menu.classList.add('hidden');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });

  const tagline = document.getElementById('header-tagline');
  const onScroll = () => {
    const scrolled = window.scrollY > 40;
    header.classList.toggle('shadow-md', scrolled);
    // The handwritten tagline sits below the header, so only show it at the top of the page.
    if (tagline) tagline.style.opacity = scrolled ? '0' : '1';
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  document.querySelectorAll('[data-year]').forEach((el) => { el.textContent = String(new Date().getFullYear()); });
}

/** Shows a message above the form: red for an error, blue for a hint. */
function showError(form, message, tone = 'error') {
  const box = form.querySelector('[data-form-error]');
  box.textContent = message || '';
  box.classList.toggle('hidden', !message);
  box.classList.toggle('bg-red-50', tone === 'error');
  box.classList.toggle('text-red-700', tone === 'error');
  box.classList.toggle('bg-sky', tone !== 'error');
  box.classList.toggle('text-blue', tone !== 'error');
}

function busy(form, isBusy) {
  form.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = isBusy; });
}

function go(defaultPath) {
  window.location.assign(returnTo || defaultPath);
}

/** Opens the sign-in dialog. `options.returnTo` is the page to come back to afterwards. */
export function openSignIn({ email = '', returnTo: back = null, message = '' } = {}) {
  returnTo = back;
  const dialog = document.getElementById('sign-in-dialog');
  const form = document.getElementById('sign-in-form');
  showError(form, message, 'hint');
  if (email) form.identifier.value = email;
  dialog.showModal();
  (email ? form.password : form.identifier).focus();
}

let toastTimer = null;

/**
 * A short message that floats at the bottom of the page. With `action`, it has a button, such as
 * "Log in". Replaces any message already showing, and goes away by itself.
 */
export function showToast(message, { action = '', onAction = null, seconds = 6 } = {}) {
  let toast = document.getElementById('toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'toast';
    toast.setAttribute('role', 'alert');
    toast.className = 'fixed bottom-6 left-1/2 z-50 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-4 rounded-full bg-ink px-5 py-3 text-sm text-white shadow-xl';
    document.body.append(toast);
  }
  toast.textContent = '';
  const text = document.createElement('span');
  text.textContent = message;
  toast.append(text);
  if (action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = action;
    button.className = 'font-semibold text-pink-200 underline underline-offset-4 hover:text-white';
    button.addEventListener('click', () => { toast.hidden = true; if (onAction) onAction(); });
    toast.append(button);
  }
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.hidden = true; }, seconds * 1000);
}

/** Tells a signed-out visitor they need to log in to like, with a button that opens the login dialog. */
export function requireSignInToLike() {
  showToast('Log in to like patterns.', {
    action: 'Log in',
    onAction: () => openSignIn({ returnTo: window.location.pathname + window.location.search }),
  });
}

/** Asks a signed-out visitor to sign in first, then come back to this page. */
export function requireSignIn() {
  openSignIn({
    returnTo: window.location.pathname + window.location.search,
    message: 'Sign in to do that. New to Patternade? Create a free account.',
  });
}

function setupDialogs() {
  const signInForm = document.getElementById('sign-in-form');
  if (!signInForm) return;

  document.querySelectorAll('[data-open-sign-in]').forEach((el) => el.addEventListener('click', () => openSignIn()));
  document.querySelectorAll('[data-close-dialog]').forEach((el) => el.addEventListener('click', () => el.closest('dialog').close()));
  // Close when clicking the dim backdrop around a dialog.
  document.querySelectorAll('dialog.modal').forEach((dialog) => dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  }));

  signInForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const identifier = signInForm.identifier.value.replace(/\s+/g, '').toLowerCase();
    const password = signInForm.password.value;
    if (!identifier || !password) return showError(signInForm, 'Enter your email or username and your password.');

    busy(signInForm, true);
    try {
      const response = await account.signIn(identifier, password);
      if (!response || !response.success || !response.token) {
        showError(signInForm, 'That email or username and password do not match.');
        return;
      }
      saveToken(response.token);
      go(AFTER_SIGN_IN);
    } catch {
      showError(signInForm, 'Something went wrong. Please try again.');
    } finally {
      busy(signInForm, false);
    }
  });
}

/** Runs on every page. Returns the signed-in user, or null. */
export function startPage() {
  const user = currentUser();
  setupHeader(user);
  setupDialogs();
  return user;
}
